// ============================================================================
// CLEO'S SECRET — PathologyReview
// File: src/pages/PathologyReview.jsx
//
// Clinician workstation for vetting AI pathology interpretations before
// they are released to the patient. Inherits the operational pattern from
// ClinicalDashboard / RetentionPage: obsidian header chrome, cream main
// canvas, two-pane workspace.
//
// Detail pane is the densest in the platform — it shows:
//   • Patient header with collection date and lab
//   • Red flags banner (when present)
//   • GP-facing clinical summary card (headline + key findings + follow-up)
//   • Markers grid with flag colour bars
//   • Patient-facing preview (greeting, picture, good news, areas, questions)
//   • GP notes textarea
//   • Approve / Reject actions
//
// API calls preserved:
//   • /api/get-pending-pathology   (load queue + stats)
//   • /api/approve-pathology        (approve or reject action)
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  FF, FS, P, T,
  Spinner,
} from '../lib/design-tokens';
import RequireAdmin from '../components/RequireAdmin';

// ───────── Helpers ─────────
function formatDate(s) {
  if (!s) return '';
  return new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function timeAgo(s) {
  if (!s) return '';
  var diff = Date.now() - new Date(s).getTime();
  var hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours < 1) return 'just now';
  if (hours < 24) return hours + 'h ago';
  return Math.floor(hours / 24) + ' days ago';
}

// ───────── Flag → palette ─────────
function flagColor(flag) {
  if (flag === 'optimal') return P.GREEN;
  if (flag === 'normal') return P.TF;
  if (flag === 'borderline') return P.AMBER;
  if (flag === 'high' || flag === 'low') return P.RED;
  return P.TF;
}
function flagBg(flag) {
  if (flag === 'optimal') return P.GREEN_BG;
  if (flag === 'normal') return 'rgba(122,96,64,0.06)';
  if (flag === 'borderline') return P.AMBER_BG;
  if (flag === 'high' || flag === 'low') return P.RED_BG;
  return 'transparent';
}
function flagLabel(flag) {
  if (flag === 'optimal') return 'Optimal';
  if (flag === 'normal') return 'Normal';
  if (flag === 'borderline') return 'Borderline';
  if (flag === 'high') return 'High';
  if (flag === 'low') return 'Low';
  return flag;
}

// ───────── Patient display name ─────────
function patientName(patient, fallbackEmail) {
  if (patient && (patient.first_name || patient.last_name)) {
    return (patient.first_name + ' ' + (patient.last_name || '')).trim();
  }
  return fallbackEmail || 'Unknown patient';
}

// ───────── SectionLabel ─────────
function SectionLabel({ children }) {
  return (
    <div style={{
      fontFamily: FF,
      fontSize: 'clamp(11px, 0.95vw, 13px)',
      fontWeight: 700,
      letterSpacing: '0.32em',
      textTransform: 'uppercase',
      color: P.BR2,
      marginBottom: 'clamp(14px, 1.6vw, 18px)',
      paddingBottom: 'clamp(10px, 1.2vw, 14px)',
      borderBottom: '1px solid ' + P.SAND3,
    }}>{children}</div>
  );
}

// ───────── Diamond bulleted list (Stage 2 style) ─────────
function BulletList({ items, color }) {
  var bulletColor = color || P.BR;
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {items.map(function(item, i) {
        return (
          <li key={i} style={{
            position: 'relative',
            paddingLeft: 22,
            marginBottom: i === items.length - 1 ? 0 : 10,
            fontFamily: FF,
            fontSize: 'clamp(13px, 1.1vw, 15px)',
            color: P.TX,
            lineHeight: 1.7,
          }}>
            <span style={{
              position: 'absolute',
              left: 4,
              top: '0.55em',
              width: 7,
              height: 7,
              background: bulletColor,
              transform: 'rotate(45deg)',
            }} />
            {item}
          </li>
        );
      })}
    </ul>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// PathologyReview — outer wrapper. Gates on patients.is_admin via
// RequireAdmin so the inner page (and its data-fetching useEffects) only
// mount when the caller is confirmed admin. See src/components/RequireAdmin.jsx.
// ═════════════════════════════════════════════════════════════════════════
export default function PathologyReview(props) {
  return (
    <RequireAdmin email={props.email} isAdmin={props.isAdmin} onNavigate={props.onNavigate}>
      <PathologyReviewInner onNavigate={props.onNavigate} />
    </RequireAdmin>
  );
}

function PathologyReviewInner(props) {
  var onNavigate = props.onNavigate;

  var dataState = useState(null);
  var data = dataState[0];
  var setData = dataState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var selectedState = useState(null);
  var selected = selectedState[0];
  var setSelected = selectedState[1];

  var gpNotesState = useState('');
  var gpNotes = gpNotesState[0];
  var setGpNotes = gpNotesState[1];

  var savingState = useState(false);
  var saving = savingState[0];
  var setSaving = savingState[1];

  function loadData() {
    setLoading(true);
    fetch('/api/get-pending-pathology', { method: 'POST' })
      .then(function(res) { return res.json(); })
      .then(function(result) {
        setData(result);
        setLoading(false);
      })
      .catch(function() { setLoading(false); });
  }

  useEffect(function() { loadData(); }, []);

  function selectUpload(upload) {
    setSelected(upload);
    setGpNotes(upload.interpretation && upload.interpretation.gp_notes ? upload.interpretation.gp_notes : '');
  }

  function review(action) {
    if (!selected || !selected.interpretation) return;
    setSaving(true);
    fetch('/api/approve-pathology', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        interpretationId: selected.interpretation.id,
        action: action,
        gpNotes: gpNotes || null,
        reviewedBy: 'robyn',
      }),
    })
      .then(function(res) { return res.json(); })
      .then(function() {
        setSaving(false);
        setSelected(null);
        setGpNotes('');
        loadData();
      })
      .catch(function() { setSaving(false); });
  }

  // ────────────────────────────────────────────────────────────────────────
  // LOADING STATE
  // ────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        background: P.OBS,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Spinner color={P.GO2} size={48} />
      </div>
    );
  }

  var uploads = (data && data.uploads) || [];
  var stats = (data && data.stats) || { awaiting_review: 0, processing: 0, failed: 0 };

  // ────────────────────────────────────────────────────────────────────────
  // STAT CARD
  // ────────────────────────────────────────────────────────────────────────
  function StatCard({ label, value, highlight, color }) {
    return (
      <div style={{
        flex: 1,
        minWidth: 180,
        padding: 'clamp(18px, 2vw, 24px)',
        background: highlight ? 'rgba(201, 169, 97, 0.08)' : P.PAP,
        border: '1px solid ' + (highlight ? P.GO : P.SAND3),
        borderTop: '2px solid ' + (highlight ? P.GO : P.SAND3),
      }}>
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(10px, 0.85vw, 12px)',
          fontWeight: 700,
          letterSpacing: '0.32em',
          textTransform: 'uppercase',
          color: P.BR2,
          marginBottom: 10,
        }}>{label}</div>
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(32px, 3.4vw, 44px)',
          fontWeight: 400,
          lineHeight: 1,
          letterSpacing: '-0.015em',
          color: color || P.INK,
        }}>{value}</div>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────────
  // UPLOAD CARD (list item, left pane)
  // ────────────────────────────────────────────────────────────────────────
  function UploadCard({ upload }) {
    var isSelected = selected && selected.id === upload.id;
    var p = upload.patients;
    return (
      <div
        onClick={function() { selectUpload(upload); }}
        style={{
          padding: 'clamp(16px, 1.6vw, 20px)',
          background: isSelected ? 'rgba(201, 169, 97, 0.08)' : P.PAP,
          border: '1px solid ' + (isSelected ? P.GO : P.SAND3),
          borderLeft: '3px solid ' + (
            upload.flagged_count > 0 ? P.AMBER :
            (isSelected ? P.GO : P.SAND3)
          ),
          cursor: 'pointer',
          marginBottom: 10,
          transition: 'background 0.2s, border-color 0.2s',
        }}
        onMouseEnter={function(e) {
          if (!isSelected) e.currentTarget.style.background = 'rgba(232,221,196,0.5)';
        }}
        onMouseLeave={function(e) {
          if (!isSelected) e.currentTarget.style.background = P.PAP;
        }}>
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(15px, 1.3vw, 17px)',
          fontWeight: 500,
          color: P.INK,
          lineHeight: 1.2,
          letterSpacing: '-0.005em',
          marginBottom: 6,
        }}>{patientName(p, upload.email)}</div>

        <div style={{
          fontFamily: FS,
          fontSize: 'clamp(11px, 0.95vw, 12px)',
          color: P.TF,
          letterSpacing: '0.02em',
          wordBreak: 'break-word',
          marginBottom: 10,
        }}>{upload.email}</div>

        <div style={{
          display: 'flex',
          gap: 14,
          flexWrap: 'wrap',
          fontFamily: FF,
          fontSize: 'clamp(10px, 0.85vw, 11px)',
          fontWeight: 600,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: P.TF,
        }}>
          <span style={{ fontStyle: 'italic', fontFamily: FF, letterSpacing: '0.02em', textTransform: 'none', fontWeight: 400 }}>
            {timeAgo(upload.uploaded_at)}
          </span>
          {upload.marker_count > 0 && (
            <span>{upload.marker_count} markers</span>
          )}
          {upload.flagged_count > 0 && (
            <span style={{ color: P.AMBER }}>{upload.flagged_count} flagged</span>
          )}
        </div>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────────
  // DETAIL PANE
  // ────────────────────────────────────────────────────────────────────────
  function renderDetail() {
    if (!selected) {
      return (
        <div style={{
          padding: 'clamp(60px, 8vw, 100px) clamp(24px, 3vw, 40px)',
          textAlign: 'center',
          background: 'rgba(232,221,196,0.3)',
          border: '1px dashed ' + P.SAND3,
          minHeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <p style={{
            ...T.premise,
            fontSize: 'clamp(18px, 1.8vw, 24px)',
            color: P.BR2,
            maxWidth: 420,
            margin: 0,
          }}>Select an upload to review the AI interpretation.</p>
        </div>
      );
    }

    if (!selected.interpretation || !selected.interpretation.ai_content) {
      var isFailed = selected.status === 'interpretation_failed';
      return (
        <div style={{
          padding: 'clamp(40px, 5vw, 60px) clamp(24px, 3vw, 40px)',
          textAlign: 'center',
          background: isFailed ? P.RED_BG : 'rgba(232,221,196,0.3)',
          border: '1px solid ' + (isFailed ? P.RED : P.SAND3),
          borderLeft: '3px solid ' + (isFailed ? P.RED : P.SAND3),
          minHeight: 240,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            color: isFailed ? P.RED : P.BR2,
            marginBottom: 14,
          }}>{isFailed ? 'Interpretation failed' : 'In progress'}</div>
          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(15px, 1.4vw, 18px)',
            fontStyle: 'italic',
            color: isFailed ? P.RED : P.BR2,
            lineHeight: 1.6,
            margin: 0,
            maxWidth: 420,
          }}>
            {isFailed
              ? 'The file may not have been readable. The patient may need to re-upload.'
              : 'The interpretation is still being prepared. Check back shortly.'}
          </p>
        </div>
      );
    }

    var ai = selected.interpretation.ai_content;
    var patient = selected.patients;
    var ps = ai.patient_summary;
    var gs = ai.gp_summary;
    var hasRedFlags = gs && gs.red_flags && gs.red_flags !== 'None identified' && gs.red_flags.length > 5;

    return (
      <div>

        {/* ─── Patient header ─── */}
        <div style={{ marginBottom: 'clamp(24px, 2.6vw, 32px)' }}>
          <h2 style={{
            fontFamily: FF,
            fontSize: 'clamp(24px, 2.4vw, 32px)',
            fontWeight: 400,
            color: P.INK,
            lineHeight: 1.15,
            letterSpacing: '-0.012em',
            margin: '0 0 6px',
          }}>{patientName(patient, selected.email)}</h2>
          <div style={{
            fontFamily: FS,
            fontSize: 'clamp(12px, 1vw, 14px)',
            color: P.TF,
            letterSpacing: '0.02em',
            wordBreak: 'break-word',
          }}>
            {selected.email}
            {selected.collection_date && ' \u00b7 Collected ' + formatDate(selected.collection_date)}
            {selected.laboratory && ' \u00b7 ' + selected.laboratory}
          </div>
        </div>

        {/* ─── Red flags banner ─── */}
        {hasRedFlags && (
          <div style={{
            padding: 'clamp(18px, 2vw, 24px)',
            background: P.RED_BG,
            border: '1px solid ' + P.RED,
            borderLeft: '3px solid ' + P.RED,
            marginBottom: 'clamp(20px, 2.4vw, 28px)',
          }}>
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(10px, 0.85vw, 12px)',
              fontWeight: 700,
              letterSpacing: '0.32em',
              textTransform: 'uppercase',
              color: P.RED,
              marginBottom: 10,
            }}>Red flags</div>
            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(14px, 1.2vw, 16px)',
              color: P.TX,
              lineHeight: 1.7,
              margin: 0,
            }}>{gs.red_flags}</p>
          </div>
        )}

        {/* ─── GP Clinical Summary ─── */}
        {gs && (
          <section style={{ marginBottom: 'clamp(28px, 3vw, 40px)' }}>
            <SectionLabel>Clinical summary</SectionLabel>
            <div style={{
              padding: 'clamp(22px, 2.4vw, 30px)',
              background: 'rgba(201, 169, 97, 0.06)',
              border: '1px solid ' + P.GO,
              borderLeft: '3px solid ' + P.GO,
            }}>
              {gs.headline && (
                <p style={{
                  fontFamily: FF,
                  fontSize: 'clamp(17px, 1.5vw, 20px)',
                  fontWeight: 500,
                  color: P.INK,
                  lineHeight: 1.5,
                  letterSpacing: '-0.005em',
                  margin: '0 0 clamp(18px, 2vw, 24px)',
                }}>{gs.headline}</p>
              )}

              {gs.key_findings && gs.key_findings.length > 0 && (
                <div style={{ marginBottom: 'clamp(18px, 2vw, 22px)' }}>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(10px, 0.85vw, 11px)',
                    fontWeight: 700,
                    letterSpacing: '0.28em',
                    textTransform: 'uppercase',
                    color: P.BR2,
                    marginBottom: 10,
                  }}>Key findings</div>
                  <BulletList items={gs.key_findings} color={P.BR} />
                </div>
              )}

              {gs.follow_up_investigations && gs.follow_up_investigations.length > 0 && (
                <div>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(10px, 0.85vw, 11px)',
                    fontWeight: 700,
                    letterSpacing: '0.28em',
                    textTransform: 'uppercase',
                    color: P.BR2,
                    marginBottom: 10,
                  }}>Suggested follow-up</div>
                  <BulletList items={gs.follow_up_investigations} color={P.BR} />
                </div>
              )}
            </div>
          </section>
        )}

        {/* ─── Markers grid ─── */}
        {ai.markers && ai.markers.length > 0 && (
          <section style={{ marginBottom: 'clamp(28px, 3vw, 40px)' }}>
            <div style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 12,
              marginBottom: 'clamp(14px, 1.6vw, 18px)',
              paddingBottom: 'clamp(10px, 1.2vw, 14px)',
              borderBottom: '1px solid ' + P.SAND3,
            }}>
              <h3 style={{
                fontFamily: FF,
                fontSize: 'clamp(11px, 0.95vw, 13px)',
                fontWeight: 700,
                letterSpacing: '0.32em',
                textTransform: 'uppercase',
                color: P.BR2,
                margin: 0,
              }}>Markers</h3>
              <span style={{
                fontFamily: FF,
                fontSize: 'clamp(11px, 0.95vw, 13px)',
                fontWeight: 600,
                letterSpacing: '0.22em',
                color: P.TF,
              }}>{String(ai.markers.length).padStart(2, '0')}</span>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 10,
            }}>
              {ai.markers.map(function(m, i) {
                return (
                  <div key={i} style={{
                    padding: 'clamp(14px, 1.4vw, 18px)',
                    background: P.PAP,
                    border: '1px solid ' + P.SAND3,
                    borderLeft: '3px solid ' + flagColor(m.flag),
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 10,
                      marginBottom: 8,
                    }}>
                      <div style={{
                        fontFamily: FF,
                        fontSize: 'clamp(10px, 0.85vw, 11px)',
                        fontWeight: 700,
                        letterSpacing: '0.22em',
                        textTransform: 'uppercase',
                        color: P.BR2,
                        flex: 1,
                        minWidth: 0,
                      }}>{m.marker_name}</div>
                      <span style={{
                        fontFamily: FF,
                        fontSize: 'clamp(9px, 0.75vw, 10px)',
                        fontWeight: 700,
                        letterSpacing: '0.22em',
                        textTransform: 'uppercase',
                        color: flagColor(m.flag),
                        background: flagBg(m.flag),
                        border: '1px solid ' + flagColor(m.flag),
                        padding: '3px 7px',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}>{flagLabel(m.flag)}</span>
                    </div>
                    <div style={{
                      fontFamily: FF,
                      fontSize: 'clamp(18px, 1.6vw, 22px)',
                      fontWeight: 500,
                      color: P.INK,
                      lineHeight: 1.1,
                      letterSpacing: '-0.01em',
                      marginBottom: 4,
                    }}>
                      {m.value}{' '}
                      <span style={{
                        fontSize: '0.6em',
                        fontWeight: 400,
                        color: P.TF,
                        letterSpacing: 0,
                      }}>{m.unit}</span>
                    </div>
                    <div style={{
                      fontFamily: FF,
                      fontSize: 'clamp(11px, 0.95vw, 12px)',
                      fontStyle: 'italic',
                      color: P.TF,
                      marginBottom: m.ai_interpretation ? 10 : 0,
                    }}>Ref: {m.reference_range}</div>
                    {m.ai_interpretation && (
                      <div style={{
                        fontFamily: FF,
                        fontSize: 'clamp(12px, 1vw, 13px)',
                        fontStyle: 'italic',
                        color: P.TL,
                        lineHeight: 1.6,
                        marginTop: 10,
                        paddingTop: 10,
                        borderTop: '1px dashed ' + P.SAND3,
                      }}>{m.ai_interpretation}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ─── Patient-facing preview ─── */}
        {ps && (
          <section style={{ marginBottom: 'clamp(28px, 3vw, 40px)' }}>
            <SectionLabel>Patient-facing interpretation</SectionLabel>
            <div style={{
              background: P.PAP,
              border: '1px solid ' + P.SAND3,
              padding: 'clamp(22px, 2.4vw, 30px)',
            }}>
              {ps.greeting && (
                <p style={{
                  fontFamily: FF,
                  fontSize: 'clamp(15px, 1.3vw, 17px)',
                  fontStyle: 'italic',
                  color: P.BR2,
                  lineHeight: 1.55,
                  margin: '0 0 clamp(16px, 1.8vw, 20px)',
                }}>{ps.greeting}</p>
              )}

              {ps.overall_picture && (
                <p style={{
                  fontFamily: FF,
                  fontSize: 'clamp(13px, 1.1vw, 15px)',
                  color: P.TX,
                  lineHeight: 1.75,
                  margin: '0 0 clamp(18px, 2vw, 22px)',
                  textAlign: 'justify',
                  hyphens: 'auto',
                }}>{ps.overall_picture}</p>
              )}

              {ps.good_news && ps.good_news.length > 0 && (
                <div style={{ marginBottom: 'clamp(16px, 1.8vw, 22px)' }}>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(10px, 0.85vw, 11px)',
                    fontWeight: 700,
                    letterSpacing: '0.28em',
                    textTransform: 'uppercase',
                    color: P.GREEN,
                    marginBottom: 10,
                  }}>Good news</div>
                  <BulletList items={ps.good_news} color={P.GREEN} />
                </div>
              )}

              {ps.areas_to_explore && ps.areas_to_explore.length > 0 && (
                <div style={{ marginBottom: 'clamp(16px, 1.8vw, 22px)' }}>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(10px, 0.85vw, 11px)',
                    fontWeight: 700,
                    letterSpacing: '0.28em',
                    textTransform: 'uppercase',
                    color: P.AMBER,
                    marginBottom: 10,
                  }}>Areas to explore with your GP</div>
                  <BulletList items={ps.areas_to_explore} color={P.AMBER} />
                </div>
              )}

              {ps.questions_for_gp && ps.questions_for_gp.length > 0 && (
                <div style={{ marginBottom: 'clamp(16px, 1.8vw, 22px)' }}>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(10px, 0.85vw, 11px)',
                    fontWeight: 700,
                    letterSpacing: '0.28em',
                    textTransform: 'uppercase',
                    color: P.BR2,
                    marginBottom: 10,
                  }}>Questions for your consultation</div>
                  {ps.questions_for_gp.map(function(q, i) {
                    return (
                      <div key={i} style={{
                        display: 'flex',
                        gap: 14,
                        alignItems: 'flex-start',
                        marginBottom: i === ps.questions_for_gp.length - 1 ? 0 : 10,
                      }}>
                        <span style={{
                          fontFamily: FF,
                          fontSize: 'clamp(18px, 1.6vw, 22px)',
                          fontStyle: 'italic',
                          color: P.GO,
                          lineHeight: 1,
                          flexShrink: 0,
                          marginTop: 2,
                        }}>?</span>
                        <p style={{
                          fontFamily: FF,
                          fontSize: 'clamp(13px, 1.1vw, 15px)',
                          color: P.TX,
                          lineHeight: 1.65,
                          margin: 0,
                        }}>{q}</p>
                      </div>
                    );
                  })}
                </div>
              )}

              {ps.reassurance && (
                <p style={{
                  fontFamily: FF,
                  fontSize: 'clamp(14px, 1.2vw, 16px)',
                  fontStyle: 'italic',
                  color: P.TX,
                  lineHeight: 1.7,
                  margin: 'clamp(14px, 1.6vw, 18px) 0 0',
                  paddingTop: 'clamp(14px, 1.6vw, 18px)',
                  borderTop: '1px solid ' + P.SAND3,
                  textAlign: 'center',
                }}>{ps.reassurance}</p>
              )}
            </div>
          </section>
        )}

        {/* ─── GP Notes textarea ─── */}
        <section style={{ marginBottom: 'clamp(20px, 2.4vw, 28px)' }}>
          <label style={{
            display: 'block',
            fontFamily: FF,
            fontSize: 'clamp(10px, 0.85vw, 11px)',
            fontWeight: 700,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: P.BR2,
            marginBottom: 8,
          }}>
            Your notes
            <span style={{
              fontFamily: FF,
              fontStyle: 'italic',
              fontWeight: 400,
              textTransform: 'none',
              letterSpacing: '0.02em',
              color: P.TF,
              fontSize: '0.95em',
              marginLeft: 6,
            }}>(optional)</span>
          </label>
          <textarea
            value={gpNotes}
            onChange={function(e) { setGpNotes(e.target.value); }}
            rows={4}
            placeholder={'Add any notes or modifications before approving\u2026'}
            style={{
              width: '100%',
              padding: '14px 16px',
              fontFamily: FF,
              fontSize: 'clamp(14px, 1.2vw, 16px)',
              color: P.INK,
              background: P.PAP,
              border: '1px solid ' + P.SAND3,
              outline: 'none',
              resize: 'vertical',
              boxSizing: 'border-box',
              lineHeight: 1.7,
              transition: 'border-color 0.2s',
            }}
            onFocus={function(e) { e.currentTarget.style.borderColor = P.BR; }}
            onBlur={function(e) { e.currentTarget.style.borderColor = P.SAND3; }}
          />
        </section>

        {/* ─── Action buttons ─── */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            onClick={function() { review('reject'); }}
            disabled={saving}
            style={{
              flex: 1,
              minWidth: 140,
              fontFamily: FS,
              fontSize: 'clamp(11px, 0.95vw, 12px)',
              fontWeight: 600,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              padding: '14px 24px',
              background: 'transparent',
              color: P.RED,
              border: '1px solid ' + P.RED,
              cursor: saving ? 'default' : 'pointer',
              opacity: saving ? 0.5 : 1,
              transition: 'background 0.2s',
            }}
            onMouseEnter={function(e) { if (!saving) e.currentTarget.style.background = P.RED_BG; }}
            onMouseLeave={function(e) { if (!saving) e.currentTarget.style.background = 'transparent'; }}>
            Reject
          </button>
          <button
            onClick={function() { review('approve'); }}
            disabled={saving}
            style={{
              flex: 2,
              minWidth: 240,
              fontFamily: FS,
              fontSize: 'clamp(11px, 0.95vw, 12px)',
              fontWeight: 600,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              padding: '14px 24px',
              background: P.GO,
              color: P.OBS,
              border: 'none',
              cursor: saving ? 'default' : 'pointer',
              opacity: saving ? 0.5 : 1,
              transition: 'background 0.2s',
            }}
            onMouseEnter={function(e) { if (!saving) e.currentTarget.style.background = P.GO2; }}
            onMouseLeave={function(e) { if (!saving) e.currentTarget.style.background = P.GO; }}>
            {saving ? 'Saving\u2026' : 'Approve and release to patient'}
          </button>
        </div>

      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ background: P.PAP, minHeight: '100vh', fontFamily: FF }}>

      {/* ═════════ Obsidian header ═════════ */}
      <header style={{
        background: P.OBS,
        borderBottom: '1px solid ' + P.GO,
        padding: 'clamp(20px, 2.4vw, 32px) clamp(24px, 3vw, 40px)',
      }}>
        <div style={{
          maxWidth: 1400,
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}>
          <div>
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(10px, 0.85vw, 12px)',
              fontWeight: 700,
              letterSpacing: '0.36em',
              textTransform: 'uppercase',
              color: P.GO2,
              marginBottom: 6,
            }}>Cleo's Secret &middot; Workstation</div>
            <h1 style={{
              fontFamily: FF,
              fontSize: 'clamp(22px, 2.4vw, 32px)',
              fontWeight: 400,
              color: P.PAP,
              lineHeight: 1.15,
              letterSpacing: '-0.008em',
              margin: 0,
            }}>Pathology <span style={{ fontStyle: 'italic', color: P.GO2 }}>review.</span></h1>
          </div>

          <button
            onClick={function() { if (onNavigate) onNavigate('clinical'); }}
            style={{
              fontFamily: FS,
              fontSize: 'clamp(11px, 0.9vw, 12px)',
              fontWeight: 600,
              letterSpacing: '0.24em',
              textTransform: 'uppercase',
              padding: '12px 22px',
              background: 'transparent',
              color: P.GO2,
              border: '1px solid ' + P.GO2,
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
            onMouseEnter={function(e) { e.currentTarget.style.background = 'rgba(224, 198, 133, 0.1)'; }}
            onMouseLeave={function(e) { e.currentTarget.style.background = 'transparent'; }}>
            Dashboard
          </button>
        </div>
      </header>

      {/* ═════════ Main canvas ═════════ */}
      <main style={{
        maxWidth: 1400,
        margin: '0 auto',
        padding: 'clamp(28px, 3.4vw, 48px) clamp(24px, 3vw, 40px) clamp(48px, 6vw, 80px)',
      }}>

        {/* Stats strip */}
        <div style={{
          display: 'flex',
          gap: 12,
          marginBottom: 'clamp(28px, 3.4vw, 40px)',
          flexWrap: 'wrap',
        }}>
          <StatCard
            label="Awaiting review"
            value={stats.awaiting_review}
            highlight={stats.awaiting_review > 0}
            color={stats.awaiting_review > 0 ? P.BR2 : P.INK} />
          <StatCard label="Processing" value={stats.processing} />
          <StatCard
            label="Failed"
            value={stats.failed}
            color={stats.failed > 0 ? P.RED : P.INK} />
        </div>

        {/* Two-pane workspace */}
        <div className="cs-pathology-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(320px, 380px) 1fr',
          gap: 'clamp(24px, 2.6vw, 36px)',
          alignItems: 'start',
        }}>

          {/* Left pane: upload queue */}
          <div>
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(11px, 0.95vw, 13px)',
              fontWeight: 700,
              letterSpacing: '0.32em',
              textTransform: 'uppercase',
              color: P.BR2,
              marginBottom: 'clamp(14px, 1.6vw, 18px)',
            }}>Uploads &middot; {uploads.length}</div>

            {uploads.length === 0 ? (
              <div style={{
                padding: 'clamp(36px, 4vw, 56px) clamp(20px, 2.4vw, 28px)',
                textAlign: 'center',
                background: 'rgba(232,221,196,0.3)',
                border: '1px dashed ' + P.SAND3,
              }}>
                <p style={{
                  fontFamily: FF,
                  fontSize: 'clamp(15px, 1.3vw, 17px)',
                  fontStyle: 'italic',
                  color: P.BR2,
                  margin: 0,
                }}>No uploads waiting.</p>
              </div>
            ) : (
              uploads.map(function(u) { return <UploadCard key={u.id} upload={u} />; })
            )}
          </div>

          {/* Right pane: detail */}
          <div className="cs-pathology-detail" style={{
            position: 'sticky',
            top: 24,
            maxHeight: 'calc(100vh - 48px)',
            overflowY: 'auto',
            paddingLeft: 'clamp(20px, 2.2vw, 28px)',
            borderLeft: '1px solid ' + P.SAND3,
          }}>
            {renderDetail()}
          </div>

        </div>
      </main>

      {/* Responsive */}
      <style>{`
        @media (max-width: 900px) {
          .cs-pathology-grid {
            grid-template-columns: 1fr !important;
          }
          .cs-pathology-detail {
            position: static !important;
            border-left: none !important;
            border-top: 1px solid ${P.SAND3};
            padding-left: 0 !important;
            padding-top: clamp(20px, 2.4vw, 28px);
            max-height: none !important;
          }
        }
      `}</style>
    </div>
  );
}
